import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';

import Loading from '../../../feedback/loading.jsx';
import {
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/alert-dialog.jsx';
import { Button } from '../../../ui/button.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';
import { PasswordInput } from '../../../ui/input-password.jsx';
import PortalHeading from '../heading.jsx';

export const FormAccountActionNewPassword = () => {
  const { logout } = useSnapshot(authState);

  const formAccountNewPassword = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        password: yup
          .string()
          .required('Por favor insira a sua nova palavra-passe.')
          .min(
            8,
            ({ min }) =>
              `A palavra-passe deve conter pelo menos ${min} caracteres`,
          )
          .max(
            250,
            ({ max }) =>
              `A palavra-passe não deve ultrapassar os ${max} caracteres`,
          )
          .matches(
            /\w*[A-Z]\w*/,
            'A palavra-passe deve conter pelo menos 1 letra maiúscula',
          )
          .matches(
            /\w*[a-z]\w*/,
            'A palavra-passe deve conter pelo menos 1 letra minúscula',
          )
          .matches(/\d/, 'A palavra-passe deve conter pelo menos 1 número')
          .matches(
            /[!+@#$%^&*()\-_"=+{}; :,<.>]/,
            'A palavra-passe deve conter pelo menos 1 um caracter especial',
          ),
        confirmPassword: yup
          .string()
          .required('Por favor insira a sua palavra-passe novamente.')
          .oneOf([yup.ref('password')], 'As palavras-passes não coincidem.'),
      }),
    ),
  });

  const mutationUpdateAccountPassword = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.post(`/auth/update-password`, {
        password: data.password,
      });

      return true;
    },
    onSuccess: () => {
      setTimeout(async () => {
        await logout();
      }, 5000);
    },
    onError: () => {
      toast.error('Ocorreu um erro ao alterar a palavra-passe da sua conta', {
        description: 'Por favor tente novamente mais tarde',
      });
    },
  });

  return mutationUpdateAccountPassword.isPending ? (
    <div className="flex w-full justify-center">
      <Loading message="A mudar a palavra-passe da sua conta" />
    </div>
  ) : mutationUpdateAccountPassword.isSuccess ? (
    <div className="flex flex-col items-center">
      <CheckIcon className="size-40 text-success" />
      <PortalHeading className="text-center !text-2xl">
        Palavra-passe alterada com sucesso.
      </PortalHeading>

      <div className="py-4 text-center">
        <p>
          A próxima vez que entrar na sua conta terá de usar a nova
          palavra-passe que definiu.
        </p>
      </div>
    </div>
  ) : (
    <Form key="new-password" {...formAccountNewPassword}>
      <form
        name="new-password"
        className="space-y-6"
        onSubmit={formAccountNewPassword.handleSubmit((data) => {
          mutationUpdateAccountPassword.mutate(data);
        })}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Editar palavra-passe</AlertDialogTitle>
        </AlertDialogHeader>
        <FormField
          control={formAccountNewPassword.control}
          name="password"
          render={({ field }) => (
            <FormItem variant="portal">
              <FormLabel required={true}>Nova palavra-passe</FormLabel>
              <FormControl>
                <PasswordInput {...field} autoComplete="new-password" />
              </FormControl>

              <FormMessage />

              <ul className="list-disc pl-8 text-sm text-muted-foreground">
                <li>A palavra-passe deve conter pelo menos 8 caracteres</li>
                <li>A palavra-passe não deve ultrapassar os 250 caracteres</li>
                <li>
                  A palavra-passe deve conter pelo menos uma letra maiúscula e
                  minúscula
                </li>
                <li>A palavra-passe deve conter pelo menos um número</li>
                <li>
                  A palavra-passe deve conter pelo menos um caracter especial
                </li>
              </ul>
            </FormItem>
          )}
        />

        <FormField
          control={formAccountNewPassword.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem variant="portal">
              <FormLabel required={true}>
                Confirmar nova palavra-passe
              </FormLabel>
              <FormControl>
                <PasswordInput {...field} autoComplete="new-password" />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <AlertDialogFooter className="pt-4">
          <AlertDialogCancel
            disabled={!formAccountNewPassword.formState.isDirty}
            onClick={() => {
              formAccountNewPassword.reset();
            }}
          >
            Cancelar
          </AlertDialogCancel>

          <Button
            type="submit"
            variant="portal"
            disabled={!formAccountNewPassword.formState.isDirty}
          >
            Confirmar
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
};
